/**
 * What this installation offers, as the *server* reports it — LV §181, §134.
 *
 * `GET /configuration/feature-flags` is the only source. Not `import.meta.env`, not a
 * constant in this file, not a build flag: LV §134 forbids the client taking the
 * decision, and a build-time constant would mean switching a feature required a new
 * frontend release — which is the opposite of what LV §181 asks a feature flag for.
 *
 * The store is a module variable rather than React state, for the same reason the
 * session is (`auth/session.ts`): the sync engine and the service-worker registration
 * both need to read it, and neither runs inside a component tree.
 *
 * Two behaviours are worth stating, because both are easy to get wrong:
 *
 * 1. **Before the server answers**, `FEATURE_OFFLINE` reads as on and everything else
 *    as off. Not a default in the sense of "what we think" — a choice about which
 *    mistake is cheaper. Guessing offline off would mean a technician whose flag call
 *    happened to fail loses the queue that keeps their walkdown (LV §127-132);
 *    guessing a Phase 2 surface on would show a screen the backend refuses anyway.
 *    Nothing is decided here in either case: the backend refuses a replayed change
 *    regardless of what this file believes.
 * 2. **A failed call does not disable anything.** Offline is torn down only when the
 *    server actually said `false`, because unregistering the service worker and
 *    dropping its caches is destructive and a transient 500 is not a decision.
 */

import { configurationService } from "@/services";
import type { FeatureFlags } from "@/types/api";

export type FeatureName = keyof FeatureFlags;

/** See note 1 in the module docstring — the values in force until the server answers. */
const UNRESOLVED: FeatureFlags = {
  FEATURE_OFFLINE: true,
  FEATURE_EXTERNAL_PORTAL: false,
  FEATURE_SCADA_IMPORT: false,
  FEATURE_QR_CODES: false,
};

let flags: FeatureFlags = { ...UNRESOLVED };
let answered = false;
const listeners = new Set<() => void>();

/** Fetch the flags and publish them. Never throws — a failure leaves the pre-answer values. */
export async function loadFeatureFlags(): Promise<FeatureFlags> {
  try {
    const received = await configurationService.featureFlags();
    // Spread over the known shape rather than assigning the response: a backend that
    // gains a fifth flag must not leave a key missing here, and one that loses a flag
    // must not leave `undefined` where a boolean is read.
    flags = { ...UNRESOLVED, ...received };
    answered = true;
  } catch {
    flags = { ...UNRESOLVED };
    answered = false;
  }
  for (const listener of listeners) listener();
  return flags;
}

export function isFeatureEnabled(name: FeatureName): boolean {
  return flags[name];
}

/** Whether the values in force came from the server, or are still the pre-answer ones. */
export function featureFlagsAnswered(): boolean {
  return answered;
}

/**
 * The current flags, as a stable reference.
 *
 * Deliberately *not* a copy. `useFeatureFlags` feeds this to `useSyncExternalStore`,
 * which compares snapshots by identity: a fresh object on every call is an infinite
 * render loop. Everything in this module replaces `flags` rather than mutating it, so
 * the reference changes exactly when the values do. Treat the result as read-only.
 */
export function getFeatureFlags(): FeatureFlags {
  return flags;
}

/** Re-render on change. Used by the shell, which draws the offline affordances. */
export function onFeatureFlags(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Offline operation — the service worker, the IndexedDB queue, the sync engine and
 * the sync screens (LV §127-132).
 *
 * Its own function because it is read in six places and `isFeatureEnabled(
 * "FEATURE_OFFLINE")` at each of them is six chances to check the wrong flag.
 */
export function offlineEnabled(): boolean {
  return isFeatureEnabled("FEATURE_OFFLINE");
}

/**
 * Set the flags directly. For tests and for the sign-out path only.
 *
 * The application must never call this to *decide* anything — that would be exactly
 * the client-side decision LV §134 forbids. It exists so a test can put the store in
 * a known state without a fetch, the way `offline/db.ts` exposes `resetForTests`.
 */
export function setFeatureFlags(next: Partial<FeatureFlags>, fromServer = true): void {
  flags = { ...flags, ...next };
  answered = fromServer;
  for (const listener of listeners) listener();
}

/** Back to the pre-answer values — used between tests. */
export function resetFeatureFlags(): void {
  flags = { ...UNRESOLVED };
  answered = false;
  for (const listener of listeners) listener();
}
